import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { captureException } from '@sentry/angular';
import {
  CallParticipant,
  CallProvider,
  RTCDevices,
  RTCInterface,
  TrackType,
} from '../common/interfaces/rtc-interface';
import { User } from '../models/user';
import { DEFAULT_DEVICE_ID } from '../common/utils/devices-handle-util';
import { DailyService } from './daily.service';
import { UserService } from './user.service';
import { VideoAIService } from './video-ai.service';
import { LivekitService } from './livekit.service';
import { SpaceRecordingService } from './space-recording.service';
import { TelemetryService } from './telemetry.service';
import { NoiseCancellationConfigurationService } from './noise-cancellation-configuration.service';
import {
  DeviceAndBrowserDetectorService,
  videoCallWindow,
} from './device-and-browser-detector.service';
import { MusicModeConfigurationService } from './music-mode-configuration.service';
import { ObserverModeService } from './observer-mode.service';

export enum StopVideoCallReason {
  LEAVE_CALL = 'leave_call',
  END_CALL_FOR_EVERYONE = 'end_call_for_everyone',
  LEAVE_CALL_WARNING_MODAL = 'leave_call_warning_modal',
  FORCE_LEAVE_CALL_WARNING_MODAL = 'force_leave_call_warning_modal',
  LEAVE_CALL_PROGRAMMATICALLY = 'leave_call_programmatically',
  END_CALL_EVENT_ENDED = 'end_call_event_ended',
  OBSERVER_ALONE_IN_CALL = 'observer_alone_in_call',
}

// Prefix for remote video containers
// eslint-disable-next-line frontend-rules/ngx-translate-service
const remoteVideoPrefix = 'remote-player-container-';
// eslint-disable-next-line frontend-rules/ngx-translate-service
const remoteAudioPrefix = 'remote-audio-player-container-';

@Injectable({
  providedIn: 'root',
})
export class RtcServiceController {
  service!: RTCInterface;
  user!: User;
  _provider: CallProvider = CallProvider.DAILY;
  providerChanged: BehaviorSubject<CallProvider | null> = new BehaviorSubject<CallProvider | null>(
    null,
  );
  mediaDeviceKinds: MediaDeviceKind[] = ['audioinput', 'audiooutput', 'videoinput'];
  resetOnStopVideoCallRTC = new Subject<boolean>();

  // Used BehaviorSubject to keep tracking of latest hide self view state
  private _hideSelfViewActive$ = new BehaviorSubject<boolean>(false);
  hideSelfViewActive$ = this._hideSelfViewActive$.asObservable();

  constructor(
    private injector: Injector,
    private userService: UserService,
    private videoAIService: VideoAIService,
    private telemetry: TelemetryService,
    private noiseCancellationConfigService: NoiseCancellationConfigurationService,
    private deviceAndBrowserDetectorService: DeviceAndBrowserDetectorService,
    private musicModeConfigService: MusicModeConfigurationService,
    private observerModeService: ObserverModeService,
  ) {
    // Load the default provider
    this.service = this.injector.get<RTCInterface>(DailyService);

    this.userService.user.subscribe((user) => {
      if (user) {
        this.user = user.user;
      }
    });

    this.setRTCSessionVars();
  }

  set provider(provider: CallProvider) {
    if (this._provider !== provider) {
      switch (provider) {
        case CallProvider.DAILY:
          this.service = this.injector.get<RTCInterface>(DailyService);
          break;
        case CallProvider.LIVEKIT:
          this.service = this.injector.get<RTCInterface>(LivekitService);
          break;
        default:
          this.service = this.injector.get<RTCInterface>(DailyService);
          break;
      }
      this._provider = provider;
      this.providerChanged.next(this._provider);
      this.service.isMusicModeEnabled = this.musicModeConfigService.getMusicModeState();
      this.service.isNoiseCancellationEnabled =
        this.noiseCancellationConfigService.getNoiseCancellationState();
    }
    this.setRTCSessionVars();
  }

  clearProviderChanged() {
    this.providerChanged.next(null);
  }

  stopRemoteAudio(participant: CallParticipant, trackType: TrackType): void {
    const containerId = `${remoteAudioPrefix}${trackType}-${participant.participantId}`;
    const audioPlayer = document.getElementById(containerId) as HTMLAudioElement;
    if (!audioPlayer) {
      return;
    }
    audioPlayer.pause();
  }
  // Attempts to play remote audio for a participant, the success of the function
  // is a boolean. This is used to retry again when data may be available
  async playRemoteAudio(
    track: MediaStreamTrack,
    participant: CallParticipant,
    elementId: string,
    trackType: TrackType,
  ): Promise<boolean> {
    const containerId = `${remoteAudioPrefix}${trackType}-${participant.participantId}`;
    let audioPlayer = document.getElementById(containerId) as HTMLAudioElement;

    if (!audioPlayer) {
      audioPlayer = document.createElement('audio') as HTMLAudioElement;
    }

    audioPlayer.id = containerId;
    const container = document.getElementById(elementId);

    if (!container) {
      console.error('RTC container error: could not find audio container', elementId);
      return false;
    }

    container.append(audioPlayer);
    audioPlayer.srcObject = new MediaStream([track]);

    try {
      await this.playAudio(participant, trackType, audioPlayer);
      return true;
    } catch (err) {
      return new Promise((resolve) => {
        this.telemetry.event('[Playing Audio event]', {
          participant,
          action: 'Waiting for user interaction',
        });
        document.addEventListener(
          'click',
          async () => {
            await this.playAudio(participant, trackType, audioPlayer);
            resolve(true);
          },
          { once: true },
        );
      });
    }
  }

  private async playAudio(
    participant: CallParticipant,
    trackType: TrackType,
    audioPlayer: HTMLAudioElement,
  ) {
    try {
      await audioPlayer?.play();
      this.service.attachAudioElement(participant, trackType, audioPlayer);

      this.telemetry.event('[Playing Audio event]', {
        participant,
        action: 'Playing audio succeeded',
      });
    } catch (error) {
      this.telemetry.event('[Playing Audio event]', {
        participant,
        action: 'Playing audio failed',
        error,
      });
      throw error;
    }
  }

  // Some generic functions that attach videos to containers
  static getVideoElement(participantId: string, trackType: TrackType): HTMLVideoElement {
    // WARNING: DO NOT CHANGE THIS PATTERN FOR FULLSCREEN AND SCREEN SHARING. CSS STYLING WILL BREAK.
    const videoId = `${remoteVideoPrefix}${trackType}-${participantId}`;
    // Get / Create the dom element to contain the video
    let videoPlayer = videoCallWindow().document.getElementById(videoId) as HTMLVideoElement;
    if (!videoPlayer) {
      videoPlayer = videoCallWindow().document.createElement('video') as HTMLVideoElement;
    }
    videoPlayer.id = videoId;
    videoPlayer.classList.add('peer-video-box');
    videoPlayer.muted = true;
    videoPlayer.playsInline = true;
    videoPlayer.autoplay = true;
    videoPlayer.controls = false;
    // Prevent video controls overlay on mobile devices
    videoPlayer.setAttribute('playsinline', '');
    videoPlayer.setAttribute('webkit-playsinline', '');
    videoPlayer.style.pointerEvents = 'none';
    return videoPlayer;
  }

  static getInsertedVideoElement(
    participantId: string,
    trackType: TrackType,
  ): HTMLVideoElement | undefined {
    // WARNING: DO NOT CHANGE THIS PATTERN FOR FULLSCREEN AND SCREEN SHARING. CSS STYLING WILL BREAK.
    const videoId = `${remoteVideoPrefix}${trackType}-${participantId}`;
    // Get / Create the dom element to contain the video
    return videoCallWindow().document.getElementById(videoId) as HTMLVideoElement;
  }

  public getTrack(participant: CallParticipant, trackType: TrackType) {
    switch (trackType) {
      case TrackType.AUDIO:
        return participant.audioTrack;
      case TrackType.SCREEN:
        return participant.screenVideoTrack;
      case TrackType.VIDEO:
        return participant.videoTrack;
      case TrackType.SCREEN_AUDIO:
        return participant.screenAudioTrack;
    }
  }

  // Attempts to play a video track, the success of the call is a boolean.
  // This is to indicate to the caller to retry when the data is available
  async playVideo(
    track: MediaStreamTrack,
    participant: CallParticipant,
    containerId: string,
    trackType: TrackType,
  ): Promise<boolean> {
    const videoPlayer = RtcServiceController.getVideoElement(participant.participantId, trackType);
    if (!videoPlayer) {
      return false;
    }
    const containerHTMLElement = videoCallWindow().document.getElementById(containerId);
    if (!containerHTMLElement) {
      return false;
    }
    containerHTMLElement.insertAdjacentElement('afterbegin', videoPlayer);
    try {
      await this.service.attachVideo(videoPlayer, track, participant, trackType);
      if (
        participant.userId === this.user?._id &&
        participant.video &&
        trackType === TrackType.VIDEO
      ) {
        this.videoAIService.setUserVideoElement(videoPlayer);
        await this.videoAIService.start();
      }
      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  }

  async stopVideo(participant: CallParticipant, trackType: TrackType): Promise<void> {
    const playerContainer = RtcServiceController.getVideoElement(
      participant.participantId,
      trackType,
    );
    if (playerContainer) {
      await this.service.detachVideo(playerContainer);
    }
  }

  public trackIsShared(state: string): boolean {
    return this.trackExists(state) && state !== 'hidden';
  }

  public trackExists(state: string): boolean {
    return ['playable', 'loading', 'sendable', 'interrupted', 'hidden'].includes(state);
  }

  // Passing PresenceProvider & DevicesManagerService as injecting them at the constructor will cause circular dependency
  async stopVideoCall(
    spaceRecordingService: SpaceRecordingService,
    stopVideoCallReason: StopVideoCallReason,
    opts?: {
      isBreakoutRoomChange?: boolean;
    },
  ): Promise<void> {
    this.telemetry.event('[Meeting event] Leave call', {
      stopVideoCallReason,
    });

    if (!this.service.isConnected() && !this.service.isConnecting()) {
      return;
    }

    try {
      this.resetOnStopVideoCallRTC.next(true);
      await this.service.stopScreenShare();

      if (
        spaceRecordingService.isRecordingInProgress() &&
        (spaceRecordingService.isCurrentRecordingLocal() ||
          (!spaceRecordingService.isAutomaticCloudRecordingEnabled &&
            this.service.getNumberOfParticipants() == 1 &&
            !this.observerModeService.isObserverModeActive() &&
            !opts?.isBreakoutRoomChange))
        // ^ in this case end the cloud recording because you are the only participant (Only manual CR)
      ) {
        await spaceRecordingService.stopRecording();
      }
      await this.service.leave();
    } catch (error) {
      this.telemetry.event('[Meeting event] Leave call', {
        leaveCallError: error instanceof Error ? error.message : error,
      });
    }
  }

  // Return devices list without using the providers
  async getDevices(): Promise<RTCDevices> {
    const rtcDevices: RTCDevices = {
      cameras: [],
      mics: [],
      speakers: [],
      activeCameraId: null,
      activeMicId: null,
      activeSpeakerId: null,
    };

    let devices: MediaDeviceInfo[] | undefined;
    try {
      devices = await navigator.mediaDevices.enumerateDevices();

      this.mediaDeviceKinds.forEach((kind) => {
        const specificMediaDeviceKind = devices?.filter((device) => device.kind === kind);

        this.setRtcDevices(rtcDevices, specificMediaDeviceKind || []);
      });
    } catch (enumerateDevicesError) {
      captureException(new Error('Enumerate Devices Error'), {
        extra: {
          context: JSON.stringify(enumerateDevicesError),
        },
      });
    }

    if (!this.deviceAndBrowserDetectorService.isSetSinkIdSupported()) {
      rtcDevices.speakers = [];
    }
    return rtcDevices;
  }

  private setRtcDevices(rtcDevices: RTCDevices, devices: MediaDeviceInfo[]) {
    devices.forEach((device) => {
      switch (device.kind) {
        case 'videoinput':
          rtcDevices.cameras.push(device);
          break;
        case 'audioinput':
          rtcDevices.mics.push(device);
          break;
        case 'audiooutput':
          rtcDevices.speakers.push(device);
          break;
        default:
          // unsupported device type
          break;
      }
    });
  }

  public getCurrentDeviceInfo(
    rtcDevices: RTCDevices,
    kind: MediaDeviceKind,
    groupId: string,
  ): MediaDeviceInfo | undefined {
    const devicesKind = kind === 'audioinput' ? rtcDevices.mics : rtcDevices.cameras;

    const requestedDevice = devicesKind.find(
      (device) => groupId === device.groupId && device.deviceId !== DEFAULT_DEVICE_ID,
    );

    return requestedDevice;
  }

  private setRTCSessionVars(): void {
    this.telemetry.setSessionVars({
      user_local_mic_broadcasting: Boolean(this.service.isMicEnabled()),
      user_local_cam_broadcasting: Boolean(this.service.isCameraEnabled()),
      user_in_call: Boolean(this.service.isConnected()),
      provider: this._provider === CallProvider.LIVEKIT ? 'livekit' : 'daily',
    });
  }

  toggleShowSelfVideo() {
    if (this._hideSelfViewActive$.value) {
      this.resetHideSelfViewState();
    } else {
      this.activateHideSelfView();
    }
  }

  // Used for turning off/resetting hide self view
  resetHideSelfViewState() {
    if (!this._hideSelfViewActive$.value) {
      return;
    }
    this._hideSelfViewActive$.next(false);
  }

  get hideSelfViewActive() {
    return this._hideSelfViewActive$.value;
  }

  private activateHideSelfView() {
    if (this._hideSelfViewActive$.value) {
      return;
    }
    this._hideSelfViewActive$.next(true);
  }
}

/**
 * Provides methods to test input devices by getting their streams.
 */
export const devicePreviewHelpers = {
  getVideoStream: async (cameraId: string) => {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { deviceId: cameraId, width: { ideal: 1280 }, height: { ideal: 720 } },
    });
    return stream;
  },
  getAudioStream: async (micId: string) => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { deviceId: micId },
    });
    return stream;
  },
  stopVideoStream: async (cameraId: string) => {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { deviceId: cameraId },
    });
    stream.getVideoTracks().forEach((track) => track.stop());
  },
  stopAudioStream: async (micId: string) => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { deviceId: micId },
    });
    stream.getAudioTracks().forEach((track) => track.stop());
  },
};
